import React, { useState } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import type { ShellGameAlert } from '../../hooks/useCarrierData';

interface ShellGameDetectionProps {
  alerts: ShellGameAlert[];
  checkingShellGame: boolean;
  hasCheckedShellGame: boolean;
  vehicleCount: number;
  validVinCount: number;
  batchProgress?: { current: number; total: number };
  lastCheckTime?: Date | null;
  onViewTimeline?: (vin: string) => void;
  onNavigateToCarrier?: (dotNumber: number) => void;
  onRefresh?: () => void;
  className?: string;
}

interface VinGroup {
  vin: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  carriers: Array<{
    dotNumber: number;
    legalName: string;
    firstSeen?: string;
    lastSeen?: string;
    inspectionCount?: number;
    violationCount?: number;
    oosCount?: number;
  }>;
  totalCarriers: number;
  carrierCount: number;
}

export const ShellGameDetection: React.FC<ShellGameDetectionProps> = ({
  alerts,
  checkingShellGame,
  hasCheckedShellGame,
  vehicleCount,
  validVinCount,
  batchProgress = { current: 0, total: 0 },
  lastCheckTime,
  onViewTimeline,
  onNavigateToCarrier,
  onRefresh,
  className = ''
}) => {
  const [showAllMultiCarrier, setShowAllMultiCarrier] = useState(false);

  // Calculate time since last shell game check
  const getTimeSinceCheck = (): string => {
    if (!lastCheckTime) return 'Never';

    const now = new Date();
    const diffMs = now.getTime() - lastCheckTime.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return lastCheckTime.toLocaleDateString();
  };

  // Group shell game alerts by VIN (not carrier) to avoid showing same VIN multiple times
  const groupShellGameByVIN = (alertList: ShellGameAlert[]): VinGroup[] => {
    return alertList.map(alert => ({
      vin: alert.vin,
      riskLevel: alert.riskLevel,
      carriers: alert.carriers || [],
      totalCarriers: alert.totalCarriers || alert.carriers?.length || 0,
      carrierCount: alert.totalCarriers || alert.carriers?.length || 0
    })).sort((a, b) => {
      // Sort by risk level first (CRITICAL > HIGH > MEDIUM > LOW)
      const riskOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      const riskDiff = riskOrder[b.riskLevel as keyof typeof riskOrder] - riskOrder[a.riskLevel as keyof typeof riskOrder];
      if (riskDiff !== 0) return riskDiff;
      // Then by carrier count (descending)
      return b.totalCarriers - a.totalCarriers;
    });
  };

  // SECTION: CHECKING STATE - TOP PRIORITY
  if (checkingShellGame) {
    return (
      <div className={`card-modern fade-in-up ${className}`} style={{
        background: '#e2e7d5',
        borderLeft: '4px solid #4a463f',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
          <LoadingSpinner size="sm" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 'var(--space-xs)', color: '#34312c' }}>
              Analyzing VINs Across Carrier Network...
              {batchProgress.total > 0 && (
                <span style={{ fontWeight: 400, fontSize: '0.9rem', marginLeft: '1rem' }}>
                  (Batch {batchProgress.current} of {batchProgress.total})
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
              Scanning {validVinCount} valid VINs across 8.6M VIN database for multi-carrier patterns
            </div>
            {batchProgress.total > 0 && (
              <div style={{ marginTop: 'var(--space-sm)' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(14, 165, 233, 0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(batchProgress.current / batchProgress.total) * 100}%`,
                    height: '100%',
                    background: '#4a463f',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#34312c',
                  marginTop: '4px',
                  textAlign: 'right'
                }}>
                  {Math.round((batchProgress.current / batchProgress.total) * 100)}% complete
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // SECTION: CRITICAL/HIGH FRAUD ONLY
  const criticalHighAlerts = alerts.filter(alert =>
    alert.riskLevel === 'CRITICAL' || alert.riskLevel === 'HIGH'
  );
  const totalMultiCarrier = alerts.length;

  if (criticalHighAlerts.length > 0) {
    return (
      <>
        <div className={`card-modern fade-in-up ${className}`} style={{
          background: '#f2dad2',
          borderLeft: '4px solid red',
          boxShadow: '0 4px 12px rgba(179, 65, 44, 0.2)'
        }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'start' }}>
            <AlertTriangle size={40} style={{ color: 'red', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'red', marginBottom: 'var(--space-md)' }}>
                🚨 CRITICAL FRAUD DETECTED
              </h2>
              <p style={{ fontSize: '1.125rem', marginBottom: 'var(--space-lg)', color: '#7a2b1e' }}>
                {criticalHighAlerts.length} high-risk VIN{criticalHighAlerts.length > 1 ? 's' : ''} flagged for shell game fraud
                {totalMultiCarrier > criticalHighAlerts.length && (
                  <span style={{ display: 'block', fontSize: '0.875rem', marginTop: '0.5rem', color: '#b3412c' }}>
                    ({totalMultiCarrier - criticalHighAlerts.length} additional multi-carrier VIN{(totalMultiCarrier - criticalHighAlerts.length) > 1 ? 's' : ''} require review)
                  </span>
                )}
              </p>

              {/* Group by VIN to avoid showing same VIN multiple times */}
              {groupShellGameByVIN(criticalHighAlerts).map((vinGroup, idx) => (
                <div key={idx} className="card-modern" style={{ background: '#fbf9f4', marginBottom: 'var(--space-md)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '4px' }}>
                        VIN {idx + 1} of {criticalHighAlerts.length}
                      </div>
                      <button
                        onClick={() => onViewTimeline?.(vinGroup.vin)}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: 'var(--color-primary)',
                          background: '#efebe1',
                          padding: '0.5rem 0.75rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-primary)',
                          cursor: 'pointer'
                        }}
                      >
                        {vinGroup.vin}
                      </button>
                    </div>
                    <span className="badge-modern badge-danger">
                      {vinGroup.carrierCount} Carrier{vinGroup.carrierCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ paddingLeft: 'var(--space-md)', borderLeft: '2px solid var(--color-gray-200)' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 'var(--space-sm)' }}>
                      Used by:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                      {vinGroup.carriers.map((carrier, cIdx) => (
                        <button
                          key={carrier.dotNumber || cIdx}
                          onClick={() => onNavigateToCarrier?.(carrier.dotNumber)}
                          style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            background: 'var(--color-gray-50)',
                            border: '1px solid var(--color-gray-200)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: 'var(--color-primary)',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-100)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-gray-50)'}
                        >
                          {cIdx + 1}. {carrier.legalName || `DOT ${carrier.dotNumber}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Refresh Button and View All */}
              <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-md)', borderTop: '1px solid #a39d90', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ fontSize: '0.75rem', color: '#7a2b1e' }}>
                  Last checked: {getTimeSinceCheck()}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  {totalMultiCarrier > criticalHighAlerts.length && (
                    <button
                      onClick={() => setShowAllMultiCarrier(!showAllMultiCarrier)}
                      className="btn-primary"
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        background: showAllMultiCarrier ? '#b3412c' : '#b07d2b',
                        borderColor: showAllMultiCarrier ? '#b3412c' : '#b07d2b'
                      }}
                    >
                      {showAllMultiCarrier ? 'Hide' : 'View All Multi-Carrier'} ({totalMultiCarrier})
                    </button>
                  )}
                  {onRefresh && (
                    <button
                      onClick={onRefresh}
                      disabled={checkingShellGame}
                      className="btn-secondary"
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        opacity: checkingShellGame ? 0.6 : 1,
                        cursor: checkingShellGame ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {checkingShellGame ? 'Checking...' : 'Refresh Check'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ALL MULTI-CARRIER ACTIVITY (when expanded) */}
        {showAllMultiCarrier && alerts.length > 0 && (
          <div className={`card-modern fade-in-up ${className}`} style={{
            background: '#f1e2c2',
            borderLeft: '4px solid #b07d2b'
          }}>
            <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'start' }}>
              <AlertTriangle size={32} style={{ color: '#b07d2b', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#664911', marginBottom: 'var(--space-md)' }}>
                  All Multi-Carrier Activity - {alerts.length} VINs
                </h3>
                <p style={{ fontSize: '0.875rem', marginBottom: 'var(--space-lg)', color: '#664911' }}>
                  Complete list of all VINs used by multiple carriers (includes CRITICAL, HIGH, MEDIUM, and LOW risk)
                </p>

                {/* Group all VINs - show VIN with carriers listed below */}
                {groupShellGameByVIN(alerts).map((vinGroup, idx) => {
                  const riskColors = {
                    CRITICAL: '#b3412c',
                    HIGH: '#963524',
                    MEDIUM: '#b07d2b',
                    LOW: '#5d7148'
                  };

                  return (
                    <div key={idx} className="card-modern" style={{ background: '#fbf9f4', marginBottom: 'var(--space-md)', borderLeft: `4px solid ${riskColors[vinGroup.riskLevel as keyof typeof riskColors]}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '4px' }}>
                            VIN {idx + 1} of {alerts.length}
                          </div>
                          <button
                            onClick={() => onViewTimeline?.(vinGroup.vin)}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: 'var(--color-primary)',
                              background: '#efebe1',
                              padding: '0.5rem 0.75rem',
                              borderRadius: 'var(--radius-md)',
                              border: '1px solid var(--color-primary)',
                              cursor: 'pointer'
                            }}
                          >
                            {vinGroup.vin}
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
                          <span className="badge-modern" style={{
                            background: riskColors[vinGroup.riskLevel as keyof typeof riskColors],
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {vinGroup.riskLevel}
                          </span>
                          <span className="badge-modern badge-neutral">
                            {vinGroup.carrierCount} Carrier{vinGroup.carrierCount > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <div style={{ paddingLeft: 'var(--space-md)', borderLeft: '2px solid var(--color-gray-200)' }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 'var(--space-sm)' }}>
                          Used by {vinGroup.carrierCount} carrier{vinGroup.carrierCount > 1 ? 's' : ''}:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                          {vinGroup.carriers.map((carrier, cIdx) => (
                            <button
                              key={carrier.dotNumber || cIdx}
                              onClick={() => onNavigateToCarrier?.(carrier.dotNumber)}
                              style={{
                                textAlign: 'left',
                                padding: '0.5rem',
                                background: 'var(--color-gray-50)',
                                border: '1px solid var(--color-gray-200)',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                color: 'var(--color-primary)',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-100)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-gray-50)'}
                            >
                              {cIdx + 1}. {carrier.legalName || `DOT ${carrier.dotNumber}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // SECTION: Waiting for vehicles to load
  if (!checkingShellGame && !hasCheckedShellGame && vehicleCount === 0) {
    return (
      <div className={`card-modern fade-in-up ${className}`} style={{
        background: '#e2e7d5',
        borderLeft: '4px solid #4a463f'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
          <LoadingSpinner size="sm" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#34312c', marginBottom: '4px' }}>
              Loading Fleet Data...
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
              Shell game analysis will start automatically once vehicle data loads.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SECTION: Multi-carrier activity but no critical fraud
  if (!checkingShellGame && alerts.length > 0 && hasCheckedShellGame) {
    const criticalHighCount = alerts.filter(a => a.riskLevel === 'CRITICAL' || a.riskLevel === 'HIGH').length;

    if (criticalHighCount === 0) {
      return (
        <>
          <div className={`card-modern fade-in-up ${className}`} style={{ background: '#f1e2c2', borderLeft: '4px solid #b07d2b' }}>
            <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
              <AlertTriangle size={24} style={{ color: '#b07d2b' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#664911', marginBottom: '4px' }}>
                  Multi-Carrier Activity Detected - {alerts.length} VIN{alerts.length > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#664911', marginBottom: 'var(--space-md)' }}>
                  No critical fraud detected. All {alerts.length} VIN{alerts.length > 1 ? 's' : ''} flagged as MEDIUM or LOW risk.
                </div>
                <button
                  onClick={() => setShowAllMultiCarrier(!showAllMultiCarrier)}
                  className="btn-primary"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    background: '#b07d2b',
                    borderColor: '#b07d2b'
                  }}
                >
                  {showAllMultiCarrier ? 'Hide' : 'View All Multi-Carrier'} ({alerts.length})
                </button>
              </div>
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={checkingShellGame}
                  className="btn-secondary"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem'
                  }}
                >
                  Refresh
                </button>
              )}
            </div>
          </div>

          {/* Show expanded list if toggled */}
          {showAllMultiCarrier && alerts.length > 0 && (
            <div className={`card-modern fade-in-up ${className}`} style={{
              background: '#f1e2c2',
              borderLeft: '4px solid #b07d2b'
            }}>
              <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'start' }}>
                <AlertTriangle size={32} style={{ color: '#b07d2b', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#664911', marginBottom: 'var(--space-md)' }}>
                    All Multi-Carrier Activity - {alerts.length} VINs
                  </h3>
                  <p style={{ fontSize: '0.875rem', marginBottom: 'var(--space-lg)', color: '#664911' }}>
                    Complete list of all VINs used by multiple carriers (includes MEDIUM and LOW risk)
                  </p>

                  {groupShellGameByVIN(alerts).map((vinGroup, idx) => {
                    const riskColors = {
                      CRITICAL: '#b3412c',
                      HIGH: '#963524',
                      MEDIUM: '#b07d2b',
                      LOW: '#5d7148'
                    };

                    return (
                      <div key={idx} className="card-modern" style={{ background: '#fbf9f4', marginBottom: 'var(--space-md)', borderLeft: `4px solid ${riskColors[vinGroup.riskLevel as keyof typeof riskColors]}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginBottom: '4px' }}>
                              VIN {idx + 1} of {alerts.length}
                            </div>
                            <button
                              onClick={() => onViewTimeline?.(vinGroup.vin)}
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: 'var(--color-primary)',
                                background: '#efebe1',
                                padding: '0.5rem 0.75rem',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-primary)',
                                cursor: 'pointer'
                              }}
                            >
                              {vinGroup.vin}
                            </button>
                          </div>
                          <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
                            <span className="badge-modern" style={{
                              background: riskColors[vinGroup.riskLevel as keyof typeof riskColors],
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              {vinGroup.riskLevel}
                            </span>
                            <span className="badge-modern badge-neutral">
                              {vinGroup.carrierCount} Carrier{vinGroup.carrierCount > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div style={{ paddingLeft: 'var(--space-md)', borderLeft: '2px solid var(--color-gray-200)' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 'var(--space-sm)' }}>
                            Used by {vinGroup.carrierCount} carrier{vinGroup.carrierCount > 1 ? 's' : ''}:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                            {vinGroup.carriers.map((carrier, cIdx) => (
                              <button
                                key={carrier.dotNumber || cIdx}
                                onClick={() => onNavigateToCarrier?.(carrier.dotNumber)}
                                style={{
                                  textAlign: 'left',
                                  padding: '0.5rem',
                                  background: 'var(--color-gray-50)',
                                  border: '1px solid var(--color-gray-200)',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  color: 'var(--color-primary)',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-100)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-gray-50)'}
                              >
                                {cIdx + 1}. {carrier.legalName || `DOT ${carrier.dotNumber}`}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
  }

  // SECTION: Clean state
  if (!checkingShellGame && alerts.length === 0 && vehicleCount > 0 && hasCheckedShellGame) {
    return (
      <div className={`card-modern fade-in-up ${className}`} style={{ background: '#e2e7d5', borderLeft: '4px solid green' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
          <CheckCircle size={24} style={{ color: 'var(--color-success)' }} />
          <div style={{ flex: 1, fontSize: '1rem' }}>
            No Shell Game Activity Detected - {validVinCount} VINs checked
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={checkingShellGame}
              className="btn-secondary"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                opacity: checkingShellGame ? 0.6 : 1
              }}
            >
              {checkingShellGame ? 'Checking...' : 'Refresh'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default: return null if no conditions match
  return null;
};
